import { useState } from 'react';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { Badge, Button, Card, ErrorAlert, EmptyState, Field, Modal, PageHeader, Pagination, Spinner } from '../components/ui';
import { PLATFORMS } from '@kontentowo/config';
import type { SocialAccount } from '@kontentowo/shared';

interface SharedBrands { accountId: string; brandIds: string[]; brands: { id: string; name: string }[] }

export function SocialAccountsPage() {
  const [brandId, setBrandId] = useState('');
  const { data: brands } = useAsync(() => api.brands.list(), []);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<unknown>(null);
  const [syncResult, setSyncResult] = useState<Awaited<ReturnType<typeof api.socialSync>> | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [editing, setEditing] = useState<SocialAccount | null>(null);
  const [editForm, setEditForm] = useState({ brandId: '', name: '', platform: 'OTHER', enabled: true });
  const [editSaving, setEditSaving] = useState(false);

  const [sharing, setSharing] = useState<SocialAccount | null>(null);
  const [sharedBrands, setSharedBrands] = useState<SharedBrands | null>(null);
  const [shareTarget, setShareTarget] = useState('');
  const [shareBusy, setShareBusy] = useState(false);
  const [page, setPage] = useState(1);
  const [includeDisconnected, setIncludeDisconnected] = useState(false);
  const { data, error, loading, reload } = useAsync(() => api.socialAccounts(brandId || undefined, includeDisconnected), [brandId, includeDisconnected]);

  const openEdit = (a: SocialAccount) => {
    setEditing(a);
    setEditForm({ brandId: a.brandId, name: a.name, platform: a.platform, enabled: a.enabled });
    setSyncError(null);
  };

  const saveEdit = async () => {
    if (!editing) return;
    setEditSaving(true);
    setSyncError(null);
    try {
      await api.socialUpdate(editing.id, {
        brandId: editForm.brandId,
        name: editForm.name,
        platform: editForm.platform as SocialAccount['platform'],
        enabled: editForm.enabled,
      });
      setEditing(null);
      reload();
    } catch (err) {
      setSyncError(err);
    } finally {
      setEditSaving(false);
    }
  };

  const openShare = async (a: SocialAccount) => {
    setSharing(a);
    setShareTarget('');
    setSyncError(null);
    try {
      setSharedBrands(await api.socialBrands(a.id));
    } catch (err) {
      setSharedBrands(null);
      setSyncError(err);
    }
  };

  const addSharedBrand = async () => {
    if (!sharing || !shareTarget) return;
    setShareBusy(true);
    setSyncError(null);
    try {
      await api.socialAddBrand(sharing.id, shareTarget);
      setShareTarget('');
      setSharedBrands(await api.socialBrands(sharing.id));
      reload();
    } catch (err) {
      setSyncError(err);
    } finally {
      setShareBusy(false);
    }
  };

  const removeSharedBrand = async (bid: string) => {
    if (!sharing) return;
    setShareBusy(true);
    setSyncError(null);
    try {
      await api.socialRemoveBrand(sharing.id, bid);
      setSharedBrands(await api.socialBrands(sharing.id));
      reload();
    } catch (err) {
      setSyncError(err);
    } finally {
      setShareBusy(false);
    }
  };

  const selectedBrand = brands?.find((b) => b.id === brandId) ?? null;

  const sync = async () => {
    setSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const res = await api.socialSync(brandId || undefined);
      setSyncResult(res);
      reload();
    } catch (err) {
      setSyncError(err);
    } finally {
      setSyncing(false);
    }
  };

  const toggle = async (id: string, enabled: boolean) => {
    setBusyId(id);
    try {
      await api.socialSetEnabled(id, !enabled);
      reload();
    } catch (err) {
      setSyncError(err);
    } finally {
      setBusyId(null);
    }
  };

  const removeAccount = async (account: SocialAccount) => {
    const providerNote = account.platform === 'YOUTUBE'
      ? 'Token YouTube zostanie usunięty z Kontentowo, ale konto nie zostanie odłączone w Google.'
      : 'Konto nie zostanie odłączone w integracji społecznościowej; jeśli nadal zwraca ten profil, kolejna synchronizacja może dodać go ponownie.';
    if (!window.confirm(`Usunąć „${account.name}” z Kontentowo? Usunie to konto oraz jego udostępnienia ze wszystkich marek. ${providerNote} Historia wyników publikacji pozostanie, ale starsze harmonogramy stracą przypisanie do konta.`)) return;
    setDeletingId(account.id);
    setSyncError(null);
    try {
      await api.socialDelete(account.id);
      reload();
    } catch (err) {
      setSyncError(err);
    } finally {
      setDeletingId(null);
    }
  };

  const accounts = data ?? [];

  return (
    <>
      <PageHeader
        title="Social Accounts"
        subtitle="Profile social per marka. Sync pobiera bieżący inwentarz profili z Sociamonials API (odczyt, bez publikowania)."
        actions={
          <div className="btn-row">
            {brandId && <a className="btn btn--primary" href={`/api/social-accounts/youtube/oauth/start?brandId=${encodeURIComponent(brandId)}&name=${encodeURIComponent(`${selectedBrand?.name ?? 'Brand'} YouTube`)}`}>Połącz YouTube</a>}
            <Button variant="primary" onClick={sync} disabled={syncing}>
              {syncing ? 'Synchronizacja…' : 'Sync z Sociamonials'}
            </Button>
          </div>
        }
      />

      <ErrorAlert error={syncError} />

      {syncResult && (
        <div className="alert alert--success">
          Sync OK. {syncResult.synced.reduce((a, b) => a + b.added, 0)} dodanych, {syncResult.synced.reduce((a, b) => a + b.updated, 0)} zaktualizowanych
          {syncResult.removed ? ` · ${syncResult.removed} odłączonych` : ''}
          {syncResult.duplicates ? ` · ${syncResult.duplicates} duplikatów wygaszonych` : ''}
          {syncResult.unmatched.length > 0 ? ` · ${syncResult.unmatched.length} profili nieprzypisanych do marki` : ''}.
          {syncResult.workspaceDisplayName ? ` Workspace: ${syncResult.workspaceDisplayName}.` : ''}
        </div>
      )}

      <Card title="Filtruj">
        <div className="form-row">
          <select className="select" value={brandId} onChange={(e) => setBrandId(e.target.value)}>
            <option value="">— wszystkie marki —</option>
            {brands?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          {selectedBrand && (
            <div className="inline">
              <a className="btn btn--sm" href={api.feedUrl(selectedBrand.slug, 'xml')} target="_blank" rel="noreferrer">
                RSS feed
              </a>
              <a className="btn btn--sm" href={api.feedUrl(selectedBrand.slug, 'csv')} target="_blank" rel="noreferrer">
                CSV export
              </a>
            </div>
          )}
          <label className="inline" style={{ gap: 8 }}>
            <input type="checkbox" checked={includeDisconnected} onChange={(e) => { setIncludeDisconnected(e.target.checked); setPage(1); }} />
            Pokaż odłączone / historyczne
          </label>
        </div>
      </Card>

      {loading && <Spinner />}
      {error && <ErrorAlert error={error} />}
      {!loading && accounts.length === 0 && (
        <EmptyState icon="☍" title="Brak kont" hint="Kliknij „Sync z Sociamonials”, aby pobrać profile z workspace 30175." />
      )}

      {accounts.length > 0 && (
        <Card flush>
          <table className="table">
            <thead>
              <tr>
                <th>Platforma</th>
                <th>Nazwa</th>
                <th>Brand</th>
                <th>profile_ref</th>
                <th>Workspace</th>
                <th>Status</th>
                <th style={{ textAlign: 'right' }}>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {accounts.slice((page - 1) * 12, page * 12).map((a) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 550 }}>{a.platform}</td>
                  <td>{a.name}</td>
                  <td className="muted">{brands?.find((b) => b.id === a.brandId)?.name ?? a.brandId}</td>
                  <td className="mono">{a.profileRef ?? '—'}</td>
                  <td className="mono">{a.workspaceId ?? '—'}</td>
                  <td>
                    <Badge tone={a.status === 'ACTIVE' ? 'success' : 'danger'}>{a.status}</Badge>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <Button size="sm" variant={a.enabled ? 'success' : 'ghost'} disabled={busyId === a.id} onClick={() => toggle(a.id, a.enabled)}>
                      {a.enabled ? 'enabled' : 'disabled'}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(a)}>
                      Edytuj
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openShare(a)}>
                      Udostępnij
                    </Button>
                    {a.platform === 'YOUTUBE' && a.status === 'ACTIVE' && <a className="btn btn--sm" href={`/api/social-accounts/youtube/oauth/start?accountId=${encodeURIComponent(a.id)}`}>Odśwież zgodę YouTube</a>}
                    <Button size="sm" variant="danger" disabled={deletingId === a.id} onClick={() => removeAccount(a)}>
                      {deletingId === a.id ? 'Usuwanie…' : 'Usuń konto'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={page} pageSize={12} total={accounts.length} onChange={setPage} />
        </Card>
      )}

      <Modal
        title={`Edytuj profil: ${editing?.name ?? ''}`}
        open={editing !== null}
        onClose={() => setEditing(null)}
        footer={
          <>
            <Button onClick={() => setEditing(null)}>Anuluj</Button>
            <Button variant="primary" onClick={saveEdit} disabled={editSaving || !editing}>
              {editSaving ? 'Zapisywanie…' : 'Zapisz'}
            </Button>
          </>
        }
      >
        <ErrorAlert error={syncError} />
        <Field label="Przypisz do marki">
          <select className="select" value={editForm.brandId} onChange={(e) => setEditForm({ ...editForm, brandId: e.target.value })}>
            {brands?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="form-row">
          <Field label="Nazwa profilu">
            <input className="input" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
          </Field>
          <Field label="Platforma">
            <select className="select" value={editForm.platform} onChange={(e) => setEditForm({ ...editForm, platform: e.target.value })}>
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <label className="inline" style={{ gap: 8 }}>
          <input type="checkbox" checked={editForm.enabled} onChange={(e) => setEditForm({ ...editForm, enabled: e.target.checked })} />
          Enabled
        </label>
        <div className="muted" style={{ fontSize: 12, marginTop: 10 }}>
          profile_ref: {editing?.profileRef ?? '—'} · workspace: {editing?.workspaceId ?? '—'}
        </div>
      </Modal>

      <Modal
        title={`Udostępnij profil: ${sharing?.name ?? ''}`}
        open={sharing !== null}
        onClose={() => setSharing(null)}
        footer={
          <>
            <Button onClick={() => setSharing(null)}>Zamknij</Button>
            <Button variant="primary" onClick={addSharedBrand} disabled={shareBusy || !shareTarget}>
              {shareBusy ? '…' : 'Dodaj markę'}
            </Button>
          </>
        }
      >
        <ErrorAlert error={syncError} />
        <p className="muted" style={{ fontSize: 12.5, marginTop: 0 }}>
          Główny właściciel: {brands?.find((b) => b.id === sharing?.brandId)?.name ?? sharing?.brandId ?? '—'} · To samo
          konto może publikować dla wielu marek.
        </p>
        <Field label="Dodatkowe marki (mają dostęp do tego konta)">
          {sharedBrands && sharedBrands.brands.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              {sharedBrands.brands.map((b) => (
                <span key={b.id} className="badge badge--neutral" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {b.name}
                  <button className="btn btn--sm" onClick={() => removeSharedBrand(b.id)} disabled={shareBusy}>✕</button>
                </span>
              ))}
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 12.5, marginBottom: 10 }}>Brak dodatkowych marek.</div>
          )}
        </Field>
        <Field label="Dodaj markę">
          <select className="select" value={shareTarget} onChange={(e) => setShareTarget(e.target.value)}>
            <option value="">— wybierz —</option>
            {brands
              ?.filter((b) => b.id !== sharing?.brandId && !(sharedBrands?.brandIds ?? []).includes(b.id))
              .map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
          </select>
        </Field>
      </Modal>
    </>
  );
}
